/**
 * Scalar swarm metrics (mirrors src/showoff/analysis/metrics.py).
 * Positions are flat length n*dim; velocities inferred from caller or speeds[].
 */

export function meanSpeed(speeds: ArrayLike<number>, n: number): number {
  if (n <= 0) return 0;
  let s = 0;
  for (let i = 0; i < n; i++) s += speeds[i]!;
  return s / n;
}

/** RMS distance from center of mass (radius of gyration). */
export function radiusOfGyration(
  positions: Float64Array,
  dim: number,
  n: number,
): number {
  if (n <= 0) return 0;
  const com = new Float64Array(dim);
  for (let i = 0; i < n; i++) {
    const base = i * dim;
    for (let d = 0; d < dim; d++) com[d]! += positions[base + d]!;
  }
  for (let d = 0; d < dim; d++) com[d]! /= n;

  let sum2 = 0;
  for (let i = 0; i < n; i++) {
    const base = i * dim;
    let r2 = 0;
    for (let d = 0; d < dim; d++) {
      const delta = positions[base + d]! - com[d]!;
      r2 += delta * delta;
    }
    sum2 += r2;
  }
  return Math.sqrt(sum2 / n);
}

export function meanLinkDistances(
  positions: Float64Array,
  dim: number,
  friends: Int32Array,
  enemies: Int32Array,
): { meanFriend: number; meanEnemy: number } {
  const n = friends.length;
  if (n === 0) return { meanFriend: 0, meanEnemy: 0 };
  let sf = 0;
  let se = 0;
  for (let i = 0; i < n; i++) {
    const fi = friends[i]!;
    const ei = enemies[i]!;
    let df = 0;
    let de = 0;
    const ib = i * dim;
    const fb = fi * dim;
    const eb = ei * dim;
    for (let d = 0; d < dim; d++) {
      const a = positions[ib + d]! - positions[fb + d]!;
      const b = positions[ib + d]! - positions[eb + d]!;
      df += a * a;
      de += b * b;
    }
    sf += Math.sqrt(df);
    se += Math.sqrt(de);
  }
  return { meanFriend: sf / n, meanEnemy: se / n };
}

/** Write per-particle speeds from position delta (same convention as Simulation.writeSpeeds). */
export function writeSpeedsFromDelta(
  positions: Float64Array,
  prevPositions: Float64Array,
  dim: number,
  n: number,
  out: Float64Array,
): void {
  for (let i = 0; i < n; i++) {
    let s = 0;
    const base = i * dim;
    for (let d = 0; d < dim; d++) {
      const v = positions[base + d]! - prevPositions[base + d]!;
      s += v * v;
    }
    out[i] = Math.sqrt(s);
  }
}
